import { ChangeDetectionStrategy, Component, inject, output, ViewEncapsulation } from '@angular/core';
import { CREATOR_DICTIONARY, CreatorDictionary } from '@anothertrip/anothertrip-ds';

import { environment } from '../../../environments/environment';
import { isCollabPortfolioActive } from '../../features/collaboration/collab-portfolio.availability';
import { isMyCampaignsActive, MY_CAMPAIGNS_PATH } from '../../features/marketplace/joined/my-campaigns.availability';
import { MyCampaignsAttentionService } from '../../features/marketplace/joined/services/my-campaigns-attention.service';
import { WorkspaceService } from '../../services/workspace.service';
import { DashboardNavItem, DashboardNavListComponent } from '../dashboard-nav-list/dashboard-nav-list.component';

@Component({
  selector: 'app-dashboard-nav',
  imports: [DashboardNavListComponent],
  templateUrl: './dashboard-nav.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'dashboard-nav-host',
  },
})
export class DashboardNavComponent {
  private readonly workspaceService = inject(WorkspaceService);
  private readonly myCampaignsAttention = inject(MyCampaignsAttentionService);
  protected readonly dictionary = inject<CreatorDictionary>(CREATOR_DICTIONARY);

  readonly itemClick = output<MouseEvent>();

  protected readonly workspace = this.workspaceService.workspace;

  private readonly isDevEnv = !environment.production && !environment.staging;

  // Which of these show a Beta pill is not set here: see `dashboard-nav-list/beta-nav.config.ts` (AT-2264).
  protected readonly dashboardItems: DashboardNavItem[] = [
    // Ungrouped and first. Home is the landing surface for the whole console, not a tool inside one
    // of the groups below, and filing it under a group header would say it belongs to that
    // group's job. `exact` because /dashboard is a prefix of every route under it (AT-2295).
    {
      label: 'Home',
      icon: 'home',
      routerLink: '/dashboard',
      exact: true,
    },
    {
      label: 'Instagram Tools',
      children: [
        {
          label: this.dictionary.nav.posts,
          icon: 'web_stories',
          routerLink: '/dashboard/posts',
        },
        {
          label: 'Automation',
          icon: 'manufacturing',
          routerLink: '/dashboard/automation',
        },
      ],
    },
    {
      label: 'Collaboration',
      // Records first, then finding work last: Opportunities, My Applications, My Campaigns
      // (AT-2370). The old Marketplace group folded in here; its routes are unchanged.
      children: [
        // "Collab profile", not "Profile" (AT-2637): the account chip at the sidebar's foot also reads as
        // "your profile", and this is the profile a creator collaborates through.
        {
          label: 'Collab profile',
          icon: 'id_card',
          routerLink: '/collaboration/profile',
        },
        // AT-2518: Portfolio + Review only where the Collab Portfolio is open (see its availability file).
        ...(isCollabPortfolioActive()
          ? [
              {
                label: 'Portfolio',
                icon: 'article',
                routerLink: '/collaboration/portfolio',
              },
              {
                label: 'Review',
                icon: 'inventory',
                routerLink: '/collaboration/review',
              },
            ]
          : []),
        {
          label: 'Self-audit',
          icon: 'receipt_long',
          routerLink: '/tax-audit',
        },
        {
          label: 'Invoices',
          icon: 'description',
          routerLink: '/collaboration/invoices',
        },
        // Always on (campaign creator invitations, 2026-10-15): a business can put a creator on a
        // campaign while the marketplace is still closed. See my-campaigns.availability.ts.
        ...(isMyCampaignsActive()
          ? [
              // Sentence case, like every other item (AT-2637).
              {
                label: 'My campaigns',
                icon: 'workspace_premium',
                routerLink: MY_CAMPAIGNS_PATH,
                count: this.myCampaignsAttention.needsResponseCount,
                countLabel: 'waiting for your response',
              },
            ]
          : []),
        // Dev only, like the rest of the marketplace (AT-2370): Opportunities and My Applications.
        // "Opportunities" is the planned directory of every business looking for creators, with our
        // own campaigns highlighted inside it; until it ships it opens today's campaign browse, which
        // works, so it is Beta rather than "Coming soon" (AT-2637, also AT-2594).
        ...(this.isDevEnv
          ? [
              {
                label: 'Opportunities',
                icon: 'storefront',
                routerLink: '/marketplace/browse',
              },
              {
                label: 'My applications',
                icon: 'article_person',
                routerLink: '/marketplace/applications',
              },
            ]
          : []),
      ],
    },
  ];

  constructor() {
    // Ask on every mount: a creator moving between layouts picks up an invitation that arrived meanwhile.
    if (isMyCampaignsActive()) this.myCampaignsAttention.refresh();
  }
}
